import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/use-toast';
import { CalendarIcon, Clock, User, Phone, Mail, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '@/integrations/supabase/client';

interface Specialty {
  id: string;
  name: string;
  duration_minutes: number;
  price: number;
}

interface Professional {
  id: string;
  name: string;
  specialty_id: string;
}

const Appointment = () => {
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    specialty_id: '',
    professional_id: '',
    appointment_date: undefined as Date | undefined,
    appointment_time: '',
    notes: ''
  });

  useEffect(() => {
    fetchSpecialties();
    fetchProfessionals();
  }, []);

  useEffect(() => {
    if (formData.professional_id && formData.appointment_date) {
      fetchAvailableTimes();
    }
  }, [formData.professional_id, formData.appointment_date]);

  const fetchSpecialties = async () => {
    try {
      const { data, error } = await supabase
        .from('specialties_2025_11_26_20_41')
        .select('*')
        .order('name');

      if (error) throw error;
      setSpecialties(data || []);
    } catch (error) {
      console.error('Erro ao buscar especialidades:', error);
    }
  };

  const fetchProfessionals = async () => {
    try {
      const { data, error } = await supabase
        .from('professionals_2025_11_26_20_41')
        .select('id, name, specialty_id')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setProfessionals(data || []);
    } catch (error) {
      console.error('Erro ao buscar profissionais:', error);
    }
  };

  const fetchAvailableTimes = async () => {
    if (!formData.professional_id || !formData.appointment_date) return;

    setLoading(true);
    try {
      const dateString = format(formData.appointment_date, 'yyyy-MM-dd');
      
      const { data, error } = await supabase.functions.invoke('get_available_times_2025_11_26_20_41', {
        body: {
          professional_id: formData.professional_id,
          date: dateString
        }
      });

      if (error) throw error;
      setAvailableTimes(data.available_times || []);
    } catch (error) {
      console.error('Erro ao buscar horários:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os horários disponíveis.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.client_email || !formData.client_phone || 
        !formData.specialty_id || !formData.professional_id || 
        !formData.appointment_date || !formData.appointment_time) {
      toast({
        title: "Campos obrigatórios",
        description: "Por favor, preencha todos os campos obrigatórios.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const appointmentData = {
        ...formData,
        appointment_date: format(formData.appointment_date, 'yyyy-MM-dd')
      };

      const { data, error } = await supabase.functions.invoke('create_appointment_2025_11_26_20_41', {
        body: appointmentData
      });

      if (error) throw error;

      toast({
        title: "Agendamento realizado!",
        description: "Seu agendamento foi criado com sucesso. Você receberá uma confirmação por email.",
      });

      // Reset form
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        specialty_id: '',
        professional_id: '',
        appointment_date: undefined,
        appointment_time: '',
        notes: ''
      });
      setAvailableTimes([]);

    } catch (error: any) {
      toast({
        title: "Erro no agendamento",
        description: error.message || "Ocorreu um erro ao criar o agendamento.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredProfessionals = professionals.filter(
    prof => !formData.specialty_id || prof.specialty_id === formData.specialty_id
  );

  const selectedSpecialty = specialties.find(s => s.id === formData.specialty_id);

  return (
    <section id="appointment" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Header da seção */}
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 text-primary border-primary">
            Agendamento Online
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Agende sua
            <span className="block text-primary">consulta agora</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Processo simples e rápido para agendar sua consulta. 
            Escolha o profissional, data e horário que melhor se adequam à sua rotina.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="shadow-medium">
            <CardHeader>
              <CardTitle className="text-2xl text-center">
                Formulário de Agendamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Informações pessoais */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="client_name" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Nome Completo *
                    </Label>
                    <Input
                      id="client_name"
                      value={formData.client_name}
                      onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="client_phone" className="flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefone *
                    </Label>
                    <Input
                      id="client_phone"
                      value={formData.client_phone}
                      onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                      placeholder="(11) 99999-9999"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="client_email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Email *
                  </Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={formData.client_email}
                    onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                    placeholder="seu@email.com"
                    required
                  />
                </div>

                {/* Seleção de serviço */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Especialidade *</Label>
                    <Select 
                      value={formData.specialty_id} 
                      onValueChange={(value) => setFormData({
                        ...formData, 
                        specialty_id: value,
                        professional_id: '', // Reset professional when specialty changes
                        appointment_time: '' // Reset time when professional changes
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a especialidade" />
                      </SelectTrigger>
                      <SelectContent>
                        {specialties.map((specialty) => (
                          <SelectItem key={specialty.id} value={specialty.id}>
                            <div className="flex justify-between items-center w-full">
                              <span>{specialty.name}</span>
                              <span className="text-sm text-gray-500 ml-2">
                                R$ {specialty.price?.toFixed(2)}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Profissional *</Label>
                    <Select 
                      value={formData.professional_id} 
                      onValueChange={(value) => setFormData({
                        ...formData, 
                        professional_id: value,
                        appointment_time: '' // Reset time when professional changes
                      })}
                      disabled={!formData.specialty_id}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o profissional" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProfessionals.map((professional) => (
                          <SelectItem key={professional.id} value={professional.id}>
                            {professional.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Seleção de data e horário */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="flex items-center gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      Data da Consulta *
                    </Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          disabled={!formData.professional_id}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.appointment_date ? (
                            format(formData.appointment_date, "PPP", { locale: ptBR })
                          ) : (
                            <span>Selecione a data</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.appointment_date}
                          onSelect={(date) => setFormData({
                            ...formData, 
                            appointment_date: date,
                            appointment_time: '' // Reset time when date changes
                          })}
                          disabled={(date) => date < new Date() || date.getDay() === 0} // Disable past dates and Sundays
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Horário *
                    </Label>
                    <Select 
                      value={formData.appointment_time} 
                      onValueChange={(value) => setFormData({...formData, appointment_time: value})}
                      disabled={!formData.appointment_date || loading}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={loading ? "Carregando..." : "Selecione o horário"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTimes.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                        {availableTimes.length === 0 && formData.appointment_date && !loading && (
                          <SelectItem value="no-times" disabled>
                            Nenhum horário disponível
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Informações adicionais */}
                <div>
                  <Label htmlFor="notes" className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Observações (opcional)
                  </Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Informações adicionais sobre sua consulta..."
                    rows={3}
                  />
                </div>

                {/* Resumo do agendamento */}
                {selectedSpecialty && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Resumo do Agendamento</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div>Especialidade: {selectedSpecialty.name}</div>
                      <div>Duração: {selectedSpecialty.duration_minutes} minutos</div>
                      <div>Valor: R$ {selectedSpecialty.price?.toFixed(2)}</div>
                    </div>
                  </div>
                )}

                {/* Botão de envio */}
                <Button 
                  type="submit" 
                  className="w-full gradient-primary text-white text-lg py-6"
                  disabled={submitting}
                >
                  {submitting ? 'Agendando...' : 'Confirmar Agendamento'}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  * Campos obrigatórios. Você receberá uma confirmação por email após o agendamento.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default Appointment;